export type AnchoredRect = {
  top: number;
  left: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
};

export type AnchoredMenuPos = {
  top?: number;
  bottom?: number;
  left: number;
  width: number;
  maxHeight: number;
  placement: 'above' | 'below';
};

export const ANCHORED_MENU_GAP = 4;
export const ANCHORED_MENU_MARGIN = 8;
export const ANCHORED_MENU_MIN_WIDTH = 220;
export const ANCHORED_MENU_MAX_WIDTH = 320;
export const ANCHORED_MENU_MAX_HEIGHT = 280;
const FALLBACK_MENU_HEIGHT = 160;

type PlaceAnchoredMenuArgs = {
  trigger: AnchoredRect;
  viewport: { width: number; height: number };
  menuHeight?: number;
  minWidth?: number;
  maxWidth?: number;
  maxHeight?: number;
  gap?: number;
  margin?: number;
  align?: 'start' | 'end';
  /** Keep the side chosen on open. Later scrolls must not flip the menu. */
  placement?: 'above' | 'below';
};

/**
 * Viewport-fixed coordinates for a portaled menu.
 * When flipping up, uses `bottom` so a short menu sits on the trigger
 * instead of reserving the max height and floating high on the page.
 */
export function placeAnchoredMenu({
  trigger,
  viewport,
  menuHeight,
  minWidth = ANCHORED_MENU_MIN_WIDTH,
  maxWidth = ANCHORED_MENU_MAX_WIDTH,
  maxHeight = ANCHORED_MENU_MAX_HEIGHT,
  gap = ANCHORED_MENU_GAP,
  margin = ANCHORED_MENU_MARGIN,
  align = 'start',
  placement,
}: PlaceAnchoredMenuArgs): AnchoredMenuPos {
  const width = Math.min(
    Math.max(trigger.width, minWidth),
    maxWidth,
    Math.max(margin, viewport.width - margin * 2)
  );
  let left = align === 'end' ? trigger.right - width : trigger.left;
  if (left + width > viewport.width - margin) {
    left = viewport.width - width - margin;
  }
  if (left < margin) left = margin;

  const spaceBelow = viewport.height - trigger.bottom - margin;
  const spaceAbove = trigger.top - margin;
  const needed = menuHeight ?? FALLBACK_MENU_HEIGHT;
  const openUp =
    placement === 'above' ||
    (placement !== 'below' && spaceBelow < needed && spaceAbove > spaceBelow);
  const available = Math.max(0, openUp ? spaceAbove : spaceBelow);
  const nextMaxHeight = Math.min(maxHeight, Math.max(80, available || maxHeight));

  if (openUp) {
    return {
      bottom: viewport.height - trigger.top + gap,
      left,
      width,
      maxHeight: nextMaxHeight,
      placement: 'above',
    };
  }

  return {
    top: trigger.bottom + gap,
    left,
    width,
    maxHeight: nextMaxHeight,
    placement: 'below',
  };
}

export function isSameAnchoredMenuPos(
  previous: AnchoredMenuPos | null,
  next: AnchoredMenuPos
): boolean {
  if (!previous) return false;
  return (
    previous.top === next.top &&
    previous.bottom === next.bottom &&
    previous.left === next.left &&
    previous.width === next.width &&
    previous.maxHeight === next.maxHeight &&
    previous.placement === next.placement
  );
}
